import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { BIOSDK_API_BASE_URL, CONVERTER_SINGLE_API_BASE, logBiosdkUiRoute } from './config';
import {
  QUALITY_VARIANT_GUIDE_ROWS,
  CONTEXT_IRIS_QUALITY,
  buildAcceptAttribute,
  formatListLabel,
  ISO_RECORD_LABEL,
  isPlainImageForContext,
  isIsoRecordInput,
  resolveInputKind,
  API_VARIANT_RAW,
  MODALITY_IRIS,
  NIST_QUALITY_BLOCKED_MESSAGE,
  MINEX_QUALITY_BLOCKED_MESSAGE,
  normalizeApiVariant,
} from './biosdkRoutes';
import { useBiosdkRoute } from './hooks/useBiosdkRoute';
import VariantBlockedBanner from './components/VariantBlockedBanner';
import RoutePreviewChip from './components/RoutePreviewChip';
import InputTypeBadge from './components/InputTypeBadge';
import VariantStatusBanner from './components/VariantStatusBanner';
import RequestSummaryCard from './components/RequestSummaryCard';
import VariantTestingGuide from './components/VariantTestingGuide';
import { Eye, Upload, Loader2, AlertCircle, Clock, Gauge, Info } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Label } from './components/ui/label';
import { Select } from './components/ui/select';
import { Badge } from './components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';

function normalizeAnalyticsInfo(raw) {
  if (raw == null) return {};
  if (Array.isArray(raw)) {
    const out = {};
    for (const item of raw) {
      if (item && typeof item === 'object') {
        const key = item.key ?? item.name ?? item.k;
        const val = item.value ?? item.val ?? item.v;
        if (key != null) out[String(key)] = val;
      }
    }
    return out;
  }
  if (typeof raw === 'object') return raw;
  return {};
}

function IrisQuality({ apiVariant = 'mosip' }) {
  const [file, setFile] = useState(null);
  const [irisType, setIrisType] = useState('Left');
  const [injpegImageUrl, setInJpegImageUrl] = useState(null);
  const [jp2isoImageUrl, setJp2IsoImageUrl] = useState(null);
  const [convertedIsoBytes, setConvertedIsoBytes] = useState(null);
  const [timing, setTiming] = useState({ convertInput: 0, qualityCheck: 0 });
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [qualityResult, setQualityResult] = useState(null);
  const [requestSummary, setRequestSummary] = useState(null);

  const fileExtension = file ? file.name.split('.').pop().toLowerCase() : '';
  const fileExtensions = useMemo(() => (fileExtension ? [fileExtension] : []), [fileExtension]);
  const { route, variantStatus, makeRequestSummary } = useBiosdkRoute(
    BIOSDK_API_BASE_URL,
    'process',
    fileExtensions,
    apiVariant,
    CONTEXT_IRIS_QUALITY,
    MODALITY_IRIS
  );

  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.length; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setQualityResult(null);
    setRequestSummary(null);
    setError(null);
    setInJpegImageUrl(null);
    setJp2IsoImageUrl(null);
    setConvertedIsoBytes(null);

    if (!selectedFile) {
      setError('No file selected.');
      return;
    }

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    let convertInputDuration = 0;

    try {
      if (isPlainImageForContext(fileExtension, CONTEXT_IRIS_QUALITY)) {
        setInJpegImageUrl(URL.createObjectURL(selectedFile));
      } else if (isIsoRecordInput(fileExtension, CONTEXT_IRIS_QUALITY)) {
        setConvertedIsoBytes(null);
        setInJpegImageUrl(null);
        setJp2IsoImageUrl(null);
      } else if (resolveInputKind(fileExtension, CONTEXT_IRIS_QUALITY) === 'unsupported') {
        setError('JP2 is not supported in direct mode. Use plain images or an ISO record.');
        setFile(null);
        return;
      } else {
        setError('Unsupported file type.');
        setFile(null);
        return;
      }

      setTiming((prev) => ({ ...prev, convertInput: convertInputDuration }));
    } catch (err) {
      setError('An error occurred while processing the file.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setError(null);
    setQualityResult(null);

    if (!file) {
      setError('Please upload an iris image file.');
      setIsProcessing(false);
      return;
    }

    if (variantStatus?.isRawBlocked || variantStatus?.isNistBlocked || variantStatus?.isMinexBlocked) {
      setError(variantStatus.blockedReason);
      setIsProcessing(false);
      return;
    }

    const fileExtension = file.name.split('.').pop().toLowerCase();
    let irisbdbPlaceholder = '';
    let convertMs = 0;

    try {
      const tStart = performance.now();
      if (isPlainImageForContext(fileExtension, CONTEXT_IRIS_QUALITY)) {
        const imageArrayBuffer = await file.arrayBuffer();
        convertMs = performance.now() - tStart;
        irisbdbPlaceholder = JSON.stringify(Array.from(new Uint8Array(imageArrayBuffer)));
      } else if (isIsoRecordInput(fileExtension, CONTEXT_IRIS_QUALITY)) {
        const rawOrConverted = convertedIsoBytes || (await file.arrayBuffer());
        convertMs = performance.now() - tStart;
        irisbdbPlaceholder = JSON.stringify(Array.from(new Uint8Array(rawOrConverted)));
      } else {
        throw new Error('Unsupported file type. Use plain images or an ISO record.');
      }

      const templateResponse = await fetch('/iris-quality-request.json');
      if (!templateResponse.ok) throw new Error(`Template fetch failed with status: ${templateResponse.status}`);

      const templateText = await templateResponse.text();
      const updatedTemplate = templateText.replace(/<iris_subtype>/g, irisType).replace(/irisbdb/g, irisbdbPlaceholder);

      const versionRequestTemplateResponse = await fetch('/extract_request-template.json');
      if (!versionRequestTemplateResponse.ok) {
        throw new Error(`Version request template fetch failed with status: ${versionRequestTemplateResponse.status}`);
      }

      const versionRequestTemplateText = await versionRequestTemplateResponse.text();
      const base64Encoded = arrayBufferToBase64(new TextEncoder().encode(updatedTemplate));
      const finalRequestBody = JSON.parse(versionRequestTemplateText.replace('encoded_request', base64Encoded));
      finalRequestBody.operation = 'check-quality';

      const qStart = performance.now();
      const qualityUrl = route.url;
      logBiosdkUiRoute('IrisQuality', qualityUrl.includes('/raw/') ? 'rawRoute' : 'isoRoute', qualityUrl, {
        operation: 'check-quality',
      });
      const extractResponse = await axios.post(qualityUrl, finalRequestBody, {
        headers: { 'Content-Type': 'application/json' },
      });
      const qualityMs = performance.now() - qStart;

      const inner = extractResponse.data?.response?.response;
      const irisScore = inner?.scores?.IRIS;
      if (!irisScore) {
        const errMsg = inner?.statusMessage || extractResponse.data?.errors?.[0]?.message;
        throw new Error(
          errMsg ||
            'Quality check did not return IRIS scores (is OpenIris reachable from BioSDK and URL set correctly?)'
        );
      }

      const { score, analyticsInfo: rawAnalytics } = irisScore;
      const analytics = normalizeAnalyticsInfo(rawAnalytics);

      const getQualityLevel = (s) => {
        if (s >= 80) return { level: 'Excellent', color: 'text-green-600', bg: 'bg-green-500' };
        if (s >= 60) return { level: 'Good', color: 'text-blue-600', bg: 'bg-blue-500' };
        if (s >= 40) return { level: 'Fair', color: 'text-yellow-600', bg: 'bg-yellow-500' };
        return { level: 'Poor', color: 'text-red-600', bg: 'bg-red-500' };
      };

      const qualityLevel = getQualityLevel(Number(score) || 0);

      setQualityResult({
        score: Number(score) || 0,
        qualityLevel: qualityLevel.level,
        qualityColor: qualityLevel.color,
        qualityBg: qualityLevel.bg,
        analytics,
      });
      setRequestSummary(makeRequestSummary('check-quality', file.name));

      setTiming({
        convertInput: timing.convertInput,
        qualityCheck: qualityMs,
        convertSubmit: convertMs,
      });
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred during quality check';
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setIrisType('Left');
    setInJpegImageUrl(null);
    setJp2IsoImageUrl(null);
    setConvertedIsoBytes(null);
    setQualityResult(null);
    setRequestSummary(null);
    setError(null);
  };

  return (
    <div className="biochq-page">
      <div className="biochq-container">
        <Card className="biochq-card iris">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Gauge className="h-8 w-8 text-iris" />
              <CardTitle className="text-3xl font-bold text-gray-800">Iris Quality Checker</CardTitle>
            </div>
            <CardDescription className="text-gray-600">
              Quality via unified /process — MOSIP variant: /mosip/process
            </CardDescription>
          </CardHeader>

          <CardContent>
            <VariantBlockedBanner
              apiVariant={apiVariant}
              message={
                variantStatus?.isMinexBlocked
                  ? MINEX_QUALITY_BLOCKED_MESSAGE
                  : variantStatus?.isNistBlocked
                    ? NIST_QUALITY_BLOCKED_MESSAGE
                    : null
              }
            />
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="irisType" className="text-gray-700 font-medium">
                    Iris Type
                  </Label>
                  <Select
                    id="irisType"
                    value={irisType}
                    onChange={(e) => setIrisType(e.target.value)}
                    className="w-full"
                  >
                    <option value="Left">Left Eye</option>
                    <option value="Right">Right Eye</option>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="file" className="text-gray-700 font-medium">
                    Upload Iris Image
                  </Label>
                  <div className="upload-zone">
                    <input
                      type="file"
                      id="file"
                      accept={buildAcceptAttribute(apiVariant, CONTEXT_IRIS_QUALITY)}
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <label htmlFor="file" className="cursor-pointer">
                      <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                      {normalizeApiVariant(apiVariant) === API_VARIANT_RAW ? (
                        <p className="text-xs text-gray-400 mt-1">
                          Supported: {formatListLabel(CONTEXT_IRIS_QUALITY)} → /raw/…
                        </p>
                      ) : (
                        <>
                          <p className="text-xs text-gray-400 mt-1">
                            Plain image: {formatListLabel(CONTEXT_IRIS_QUALITY)} → /raw/…
                          </p>
                          <p className="text-xs text-gray-400">{ISO_RECORD_LABEL}</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                {file && injpegImageUrl && (
                  <div className="space-y-2">
                    <Label className="text-gray-700 font-medium">Preview</Label>
                    <div className="preview-container">
                      <img src={injpegImageUrl} alt="Uploaded iris" className="preview-image" />
                      <Badge variant="iris" className="absolute top-2 right-2">
                        {irisType} Eye
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500 text-center">{file.name}</p>
                  </div>
                )}

                {file && (
                  <div className="space-y-3">
                    <InputTypeBadge fileExtensions={fileExtensions} apiVariant={apiVariant} contextKey={CONTEXT_IRIS_QUALITY} />
                    <VariantStatusBanner variantStatus={variantStatus} />
                    {route.url && <RoutePreviewChip url={route.url} />}
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    onClick={handleSubmit}
                    disabled={!file || isProcessing || variantStatus?.isNistBlocked || variantStatus?.isMinexBlocked}
                    variant="iris"
                    className="flex-1"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Gauge className="mr-2 h-4 w-4" />
                        Check Quality
                      </>
                    )}
                  </Button>
                  <Button onClick={handleReset} variant="outline" disabled={isProcessing}>
                    Reset
                  </Button>
                </div>

                <VariantTestingGuide title="Variant testing guide (Quality)" rows={QUALITY_VARIANT_GUIDE_ROWS} />
              </div>

              <div className="space-y-6">
                {requestSummary && <RequestSummaryCard summary={requestSummary} />}
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {qualityResult && (
                  <>
                    <Card
                      className={`border-2 ${
                        qualityResult.score >= 80
                          ? 'border-green-300 bg-green-50'
                          : qualityResult.score >= 60
                            ? 'border-blue-300 bg-blue-50'
                            : qualityResult.score >= 40
                              ? 'border-yellow-300 bg-yellow-50'
                              : 'border-red-300 bg-red-50'
                      }`}
                    >
                      <CardContent className="pt-6 text-center">
                        <div className="relative inline-flex items-center justify-center">
                          <svg className="w-32 h-32 transform -rotate-90">
                            <circle
                              cx="64"
                              cy="64"
                              r="56"
                              stroke="currentColor"
                              strokeWidth="12"
                              fill="none"
                              className="text-gray-200"
                            />
                            <circle
                              cx="64"
                              cy="64"
                              r="56"
                              stroke="currentColor"
                              strokeWidth="12"
                              fill="none"
                              strokeDasharray={`${qualityResult.score * 3.52} 352`}
                              className={qualityResult.qualityColor.replace('text-', 'text-')}
                              style={{
                                stroke: qualityResult.qualityBg.includes('green')
                                  ? '#22c55e'
                                  : qualityResult.qualityBg.includes('blue')
                                    ? '#3b82f6'
                                    : qualityResult.qualityBg.includes('yellow')
                                      ? '#eab308'
                                      : '#ef4444',
                              }}
                            />
                          </svg>
                          <div className="absolute">
                            <p className={`text-3xl font-bold ${qualityResult.qualityColor}`}>
                              {qualityResult.score.toFixed(0)}
                            </p>
                            <p className="text-xs text-gray-500">out of 100</p>
                          </div>
                        </div>
                        <Badge className={`mt-4 ${qualityResult.qualityBg} text-white`}>
                          {qualityResult.qualityLevel} Quality
                        </Badge>
                      </CardContent>
                    </Card>

                    {Object.keys(qualityResult.analytics).length > 0 && (
                      <Card className="bg-gray-50">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Info className="h-4 w-4" />
                            Detailed analytics
                          </CardTitle>
                          <CardDescription className="text-xs">
                            All metrics returned by the iris quality engine ({Object.keys(qualityResult.analytics).length})
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="max-h-72 overflow-y-auto pr-2 -mr-2">
                            <table className="w-full text-xs">
                              <thead className="sticky top-0 bg-gray-50 text-gray-500 uppercase tracking-wide">
                                <tr>
                                  <th className="text-left py-1 pr-2 font-medium">Metric</th>
                                  <th className="text-right py-1 pl-2 font-medium">Value</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {Object.entries(qualityResult.analytics)
                                  .sort(([a], [b]) => a.localeCompare(b))
                                  .map(([key, value]) => (
                                    <tr key={key}>
                                      <td className="py-1 pr-2 text-gray-700 break-all">{key}</td>
                                      <td className="py-1 pl-2 text-right font-mono text-gray-900 break-all">
                                        {value === null || value === undefined ? '—' : String(value)}
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    <Card className="timing-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Timing Details
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="grid grid-cols-3 gap-4 text-sm text-center">
                        <div>
                          <p className="text-gray-600">Preview conversion</p>
                          <p className="font-medium">{timing.convertInput.toFixed(2)} ms</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Submit → ISO</p>
                          <p className="font-medium">{(timing.convertSubmit ?? 0).toFixed(2)} ms</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Quality API</p>
                          <p className="font-medium text-primary">{timing.qualityCheck.toFixed(2)} ms</p>
                        </div>
                      </CardContent>
                    </Card>

                    {qualityResult.score < 80 && (
                      <Alert variant="info">
                        <Info className="h-4 w-4" />
                        <AlertTitle>Recommendations</AlertTitle>
                        <AlertDescription>
                          <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                            <li>Ensure proper lighting and a clear view of the iris</li>
                            <li>Avoid motion blur and strong reflections</li>
                            <li>Use ISO 19794-6 compliant captures when possible</li>
                          </ul>
                        </AlertDescription>
                      </Alert>
                    )}
                  </>
                )}

                {!qualityResult && !error && (
                  <div className="empty-state">
                    <Gauge className="h-12 w-12 mb-2 opacity-50" />
                    <p className="text-sm">Upload an iris image to check quality</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default IrisQuality;
